import { useEffect, useState } from "react";
import { listCustomerUsers } from "../erpClient";
import { extractCustomerUsersArray } from "./customerUsersErpAdapter";
import { isValidMobileNumber } from "./mobileInput";

const DEFAULT_MESSAGE = "Mobile number already exists";
/** Stable default so the effect does not re-run every render when callers omit localUsers. */
const EMPTY_LOCAL_USERS = [];

function customerMatchesMobile(row, mobile) {
  return String(row?.mobile ?? "").trim() === mobile;
}

function isExcludedCustomer(row, excludeCustomerId) {
  if (excludeCustomerId == null || excludeCustomerId === "") return false;
  return String(row?.id) === String(excludeCustomerId);
}

export function useMobileExistsCheck(
  mobile,
  {
    enabled = true,
    excludeCustomerId = null,
    excludeMobile = null,
    localUsers = EMPTY_LOCAL_USERS,
  } = {},
) {
  const [exists, setExists] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setExists(false);
      setChecking(false);
      return;
    }

    const trimmed = String(mobile ?? "").trim();
    if (!isValidMobileNumber(trimmed)) {
      setExists(false);
      setChecking(false);
      return;
    }

    if (excludeMobile && trimmed === String(excludeMobile).trim()) {
      setExists(false);
      setChecking(false);
      return;
    }

    const users = Array.isArray(localUsers) ? localUsers : EMPTY_LOCAL_USERS;
    if (excludeCustomerId != null && excludeCustomerId !== "") {
      const selected = users.find(
        (row) => String(row.id) === String(excludeCustomerId),
      );
      if (selected && customerMatchesMobile(selected, trimmed)) {
        setExists(false);
        setChecking(false);
        return;
      }
    }

    const localMatch = users.some(
      (row) =>
        customerMatchesMobile(row, trimmed) &&
        !isExcludedCustomer(row, excludeCustomerId),
    );

    if (localMatch) {
      setExists(true);
      setChecking(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setChecking(true);
      try {
        const response = await listCustomerUsers({ search: trimmed });
        if (cancelled) return;

        const rows = extractCustomerUsersArray(response) || [];
        const remoteMatch = rows.some(
          (row) =>
            customerMatchesMobile(row, trimmed) &&
            !isExcludedCustomer(row, excludeCustomerId),
        );

        setExists(remoteMatch);
      } catch {
        if (!cancelled) setExists(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [mobile, enabled, excludeCustomerId, excludeMobile, localUsers]);

  return {
    exists,
    checking,
    message: exists ? DEFAULT_MESSAGE : "",
  };
}
